import { zodResolver } from "@hookform/resolvers/zod";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import {
  cn,
  NewPasswordRequiredError,
  useConfirmSignIn,
  useSignIn,
  useSocialSignIn,
} from "@theprelude/core";
import {
  Button,
  Form,
  GoogleIcon,
  InputFormField,
  LockIcon,
  notifyError,
  PasswordFormField,
} from "@theprelude/ds";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import AuthPage from "@/components/auth-page";
import { isLocal, isUs2 } from "@/lib/env";
import { tb } from "@/lib/tracking";
import { passwordSchema } from "@/lib/validators";

const signInSchema = z.object({
  email: z.string().email().optional().catch(undefined),
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/sign-in/")({
  beforeLoad: async ({ context }) => {
    const authConfig = await context.auth.getAuthConfig();

    if (authConfig.status === "authenticated") {
      return redirect({ to: "/home" });
    }
  },
  validateSearch: signInSchema,
  component: SignInPage,
});

const signInFormSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

type SignInForm = z.infer<typeof signInFormSchema>;

function SignInPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const signIn = useSignIn();
  const socialSignIn = useSocialSignIn();
  const [newPasswordRequired, setNewPasswordRequired] =
    useState<boolean>(false);

  const form = useForm<SignInForm>({
    defaultValues: {
      email: search.email,
      password: "",
    },
    resolver: zodResolver(signInFormSchema),
  });

  const { register } = form;

  if (newPasswordRequired) {
    return <NewPasswordRequiredPage />;
  }
  return (
    <AuthPage title="Sign in to Prelude">
      <main>
        <Form
          {...form}
          onSubmitValid={(data) => {
            signIn.mutate(data, {
              onSuccess: () => {
                if (search.redirect) {
                  window.location.href = search.redirect;
                } else {
                  void navigate({ to: "/", replace: true });
                }
              },
              onError: (error) => {
                if (error instanceof NewPasswordRequiredError) {
                  setNewPasswordRequired(true);
                } else {
                  notifyError({
                    message:
                      "Failed to sign in. Please check your credentials.",
                  });
                }
              },
            });
          }}
          className="flex w-full flex-col gap-2"
        >
          <InputFormField
            label="Email"
            required
            placeholder={"Required"}
            type="email"
            {...register("email")}
          />

          <PasswordFormField
            {...register("password")}
            label="Password"
            required
            placeholder={"Required"}
          />

          <div className="flex flex-col items-start justify-between gap-4 pt-4">
            <Button
              className="flex w-full justify-center"
              intent="primary"
              type="submit"
              loading={signIn.isPending}
              disabled={signIn.isPending}
            >
              Sign in
            </Button>
            <div className="flex flex-col items-end gap-4">
              <Link
                className="underline underline-offset-2"
                to="/sign-in/forgot"
              >
                Forgot your password?
              </Link>
            </div>
          </div>
        </Form>

        <div className="flex flex-col gap-4 pt-4">
          <p className="text-center">or</p>
          <Button
            intent="secondary"
            className="flex w-full justify-center bg-primary-foreground text-white dark:text-black"
            icon={<GoogleIcon />}
            onClick={() => {
              socialSignIn.mutate(
                { provider: "Google" },
                {
                  onError: (error) => {
                    notifyError({
                      message: "Failed to sign in. Please try again.",
                      error,
                    });
                  },
                },
              );
            }}
          >
            Sign in with Google
          </Button>

          <Link to="/sso">
            <Button
              className="flex w-full justify-center bg-primary-foreground text-white dark:text-black"
              icon={<LockIcon className={"text-white dark:text-black"} />}
            >
              Continue with Single Sign-On
            </Button>
          </Link>
        </div>

        <div className="flex flex-col gap-2 pt-8">
          <span className="text-lg text-secondary-foreground">
            Don&rsquo;t have an account?
          </span>
          <a
            href={
              isUs2 || isLocal
                ? "/sign-up"
                : "https://www.preludesecurity.com/free-trial"
            }
            rel="noreferrer"
            target="_self"
          >
            <Button
              intent="secondary"
              className={cn("flex w-full justify-center", tb("sign-up"))}
            >
              Sign up
            </Button>
          </a>
        </div>
      </main>
    </AuthPage>
  );
}

const newPasswordRequiredSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type NewPasswordRequiredForm = z.infer<typeof newPasswordRequiredSchema>;

function NewPasswordRequiredPage() {
  const form = useForm<NewPasswordRequiredForm>({
    resolver: zodResolver(newPasswordRequiredSchema),
  });

  const navigate = useNavigate();
  const confirmSignIn = useConfirmSignIn();
  const { register } = form;
  return (
    <AuthPage title="New Password Required">
      <main>
        <Form
          className="flex w-full flex-col gap-2"
          {...form}
          onSubmitValid={(data) => {
            confirmSignIn.mutate(
              {
                newPassword: data.password,
              },
              {
                onSuccess: () => {
                  void navigate({ to: "/", replace: true });
                },
                onError: (error) => {
                  notifyError({
                    message: "Failed to reset password.",
                    error,
                  });
                },
              },
            );
          }}
        >
          <PasswordFormField
            {...register("password")}
            label="New Password"
            required
            placeholder={"Required"}
          />
          <PasswordFormField
            {...register("confirmPassword")}
            label="Confirm Password"
            required
            placeholder={"Required"}
          />
          <Button
            className="mt-4 flex w-full justify-center"
            intent="primary"
            type="submit"
            loading={confirmSignIn.isPending}
            disabled={confirmSignIn.isPending}
          >
            Change Password
          </Button>
        </Form>
      </main>
    </AuthPage>
  );
}
